import type { ReactNode } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { ApiErrorAlert } from '../ApiErrorAlert';
import { ListSearchField } from '../ListSearchField';
import { PageHeader } from './PageHeader';
import { DataTable, type DataTableColumn } from './DataTable';
import { EmptyState } from './EmptyState';
import type { QueryLike } from './QueryBoundary';
import type { SxProps } from '@mui/material';

export interface ListPageScaffoldProps<Row> {
  title: string;
  subtitle?: ReactNode;
  /** Verilirse "Yeni X" contained butonu render edilir. */
  createLabel?: string;
  onCreate?: () => void;
  /** Başlık satırındaki ek aksiyonlar (dışa aktarma vb.). */
  headerActions?: ReactNode;
  /** Client-side arama kutusu. */
  search?: { value: string; onChange: (v: string) => void; placeholder?: string };
  /** Arama kutusunun yanındaki ek filtre/araçlar. */
  toolbar?: ReactNode;
  query: QueryLike<unknown>;
  rows: Row[];
  columns: DataTableColumn<Row>[];
  getRowKey: (row: Row) => string | number;
  rowActions?: (row: Row) => ReactNode;
  onRowClick?: (row: Row) => void;
  getRowSx?: (row: Row) => SxProps;
  emptyTitle?: string;
  emptyDescription?: ReactNode;
  /** `'section'` → sayfa H1'i yerine hafif bölüm başlığı (sekme içinde gömülü listeler). */
  headerVariant?: 'page' | 'section';
  /** Diyaloglar burada olduğu gibi render edilir. */
  children?: ReactNode;
}

/** Liste sayfası iskeleti — 24 sayfadaki 120-220 satırlık
 *  (başlık + "Yeni" + arama + hata + CircularProgress + TableContainer + boş-satır)
 *  boilerplate'ini toplar. */
export function ListPageScaffold<Row>({
  title,
  subtitle,
  createLabel,
  onCreate,
  headerActions,
  search,
  toolbar,
  query,
  rows,
  columns,
  getRowKey,
  rowActions,
  onRowClick,
  getRowSx,
  emptyTitle = 'Kayıt bulunamadı.',
  emptyDescription,
  headerVariant = 'page',
  children,
}: ListPageScaffoldProps<Row>) {
  const colCount = columns.filter((c) => !c.hidden).length + (rowActions ? 1 : 0);
  const loading = query.isPending || (query.data === undefined && !query.isError);

  const createBtn = createLabel && onCreate && (
    <Button variant="contained" startIcon={<AddIcon />} onClick={onCreate}>
      {createLabel}
    </Button>
  );

  return (
    <Box>
      {headerVariant === 'section' ? (
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
          <Typography variant="subtitle1" component="h2">
            {title}
          </Typography>
          <Stack direction="row" spacing={1}>
            {headerActions}
            {createBtn}
          </Stack>
        </Stack>
      ) : (
        <PageHeader
          title={title}
          subtitle={subtitle}
          actions={
            <>
              {headerActions}
              {createBtn}
            </>
          }
        />
      )}

      {query.isError && <ApiErrorAlert error={query.error} sx={{ mb: 2 }} />}

      {(search || toolbar) && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          sx={{ mb: 2, alignItems: { sm: 'center' } }}
        >
          {search && (
            <ListSearchField
              value={search.value}
              onChange={search.onChange}
              placeholder={search.placeholder}
            />
          )}
          {toolbar}
        </Stack>
      )}

      <DataTable
        columns={columns}
        rows={rows}
        getRowKey={getRowKey}
        loading={loading}
        rowActions={rowActions}
        onRowClick={onRowClick}
        getRowSx={getRowSx}
        emptyState={
          <EmptyState variant="row" colSpan={colCount} title={emptyTitle} description={emptyDescription} />
        }
      />

      {children}
    </Box>
  );
}
